// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import useMutate from '../../../hooks/mutations/useMutate';
import TemplateContract from '../../../utils/TemplateContract';
import { BridgeAgreement } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Agreement/Bridge';
import { getNonceFromDepositMessage, getRecipientPartyFromHookData } from '../../../utils/decode';
import useStoredNonce from '../../../hooks/queries/bridge/attestation/useStoredNonce';
import useCreateDepositAttestation from '../../../hooks/mutations/bridge/attestation/useCreateDepositAttestation';
import { ERR_CRE_DEPOSIT_ATTESTATION, SUC_CRE_DEPOSIT_ATTESTATION } from '../../../utils/strings';
import { convertToDER } from '../../../utils/encode';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  bridgeAgreement: TemplateContract<BridgeAgreement>;
};

export const CreateDepositAttestationDialog = ({ open, onClose, bridgeAgreement }: DialogProps) => {
  const [payload, setPayload] = useState<string>('');
  const [messageHash, setMessageHash] = useState<string>('');
  const [attestation, setAttestation] = useState<string>('');
  const mutate = useMutate();
  const createAttestation = useCreateDepositAttestation();
  const recipient = getRecipientPartyFromHookData(payload);
  const nonce = getNonceFromDepositMessage(payload);
  const derSignature = convertToDER(attestation);
  const usedNonce = useStoredNonce(nonce);
  const usedNoncesCid = usedNonce.data?.contractId;

  const resetState = useCallback(() => {
    setPayload('');
    setMessageHash('');
    setAttestation('');
  }, [setPayload, setMessageHash, setAttestation]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
     if (!usedNoncesCid) {
      console.error('No stored nonce contract found');
      return;
    }
     const fullPayload = {
       bridgeAgreementCid: bridgeAgreement.contractId,
       recipient,
       payload,
       messageHash,
       attestation: derSignature,
       reference: usedNoncesCid.toString(),
       usedNoncesCid,
     };
     const res = await mutate(createAttestation, fullPayload, SUC_CRE_DEPOSIT_ATTESTATION, ERR_CRE_DEPOSIT_ATTESTATION);
     handleClose();
     return res;
   }, [
     createAttestation,
     handleClose,
     mutate,
     bridgeAgreement.contractId,
     recipient,
     payload,
     messageHash,
     derSignature,
     usedNoncesCid
    ]);

  const { isLoading } = usedNonce;
  if (isLoading) return null;

  const disableSubmit =
    payload.trim() === '' ||
    messageHash.trim() === '' ||
    attestation.trim() === '' ||
    recipient === '' ||
    usedNonce.data === undefined;
  return (
    <FormDialog
      open={open}
      title="Create Deposit Attestation"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Submit"
      isSubmitDisabled={disableSubmit}
    >
       <>Recipient: {recipient}</>
      <TextInput label="payload" value={payload} setValue={setPayload} />
      <TextInput label="MessageHash" value={messageHash} setValue={setMessageHash} />
      <TextInput label="Attestation" value={attestation} setValue={setAttestation} />

    </FormDialog>
  );
};
